import { sumBy } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  DictionaryObject,
  FormTextField,
  PromptContext,
  Report,
  useEventCallback,
  useFetch,
  usePrompts,
} from '@eas/common-web';
import {
  Agenda,
  AssistantFilesView,
  CourtFileType,
  EvidenceApiUrl,
  Report as ReportDefinition,
} from '@enums';
import { useAgendas } from '../es-file/es-file-api';
import { useValidationSchema } from './dashboard-schema';

export function useReport({
  url,
  labels = [],
  skip = false,
  options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  },
}: {
  url: string;
  labels?: DictionaryObject[];
  skip?: boolean;
  options?: RequestInit;
}): { id: string; label: string; value: number }[] {
  const [result] = useFetch<Report>(url, { ...options, skip });

  const items = useMemo(
    () =>
      (result?.data ?? []).map((item: { count: string; key: string }) => ({
        id: labels.find((label) => label.name === item.key)?.id ?? item.key,
        label: item.key,
        value: parseInt(item.count, 10),
      })),
    [result, labels]
  );

  return items;
}

export function useFilesStatistics() {
  const [agenda, setAgenda] = useState(Agenda.COURT);
  const [assistantFilesView, setAsistantFilesView] = useState(
    AssistantFilesView.ACTIVE
  );
  const [agendas] = useAgendas();

  const allFilesAccordingToAgendas = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.ALL_FILES_ACCORDING_TO_AGENDAS}`,
    labels: agendas,
  });

  const courtFilesAccordingToStates = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.COURT_FILES_ACCORDING_TO_STATES}`,
  });

  const allFilesAccordingToAssistants = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.ALL_FILES_ACCORDING_TO_ASSISTANTS}`,
  });

  const disciplinaryProceedingsAccordingToStates = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.DISCIPLINARY_PROCEEDINGS_ACCORDING_TO_STATES}`,
  });

  const infAccordingToStates = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.INF_ACCORDING_TO_STATES}`,
  });

  const plenaryOpinionAccordingToStates = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.PLENARY_OPINION_ACCORDING_TO_STATES}`,
  });

  const sprAccordingToStates = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.SPR_ACCORDING_TO_STATES}`,
  });

  const activeFilesAccordingToAssistants = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.ACTIVE_FILES_ACCORDING_TO_ASSISTANTS}`,
  });

  const statesAccordingToAgendas = {
    [Agenda.COURT]: courtFilesAccordingToStates,
    [Agenda.INF]: infAccordingToStates,
    [Agenda.SPR]: sprAccordingToStates,
    [Agenda.DISCIPLINARY_PROCEEDINGS]: disciplinaryProceedingsAccordingToStates,
    [Agenda.PLENARY_OPINION]: plenaryOpinionAccordingToStates,
  };

  const assistantFilesSummary = [
    {
      id: AssistantFilesView.ACTIVE,
      label: 'Aktivní',
      value: sumBy(activeFilesAccordingToAssistants, (file) => file.value),
    },
    {
      id: AssistantFilesView.ALL,
      label: 'Všechny',
      value: sumBy(allFilesAccordingToAssistants, (file) => file.value),
    },
  ];

  const assistantFiles = {
    [AssistantFilesView.ACTIVE]: activeFilesAccordingToAssistants,
    [AssistantFilesView.ALL]: allFilesAccordingToAssistants,
  };

  return {
    agenda,
    setAgenda,
    assistantFilesView,
    setAsistantFilesView,
    assistantFilesSummary,
    assistantFiles,
    allFilesAccordingToAgendas,
    statesAccordingToAgendas,
  };
}

export function useDocumentsStatistics(lastNDays: number) {
  const outgoingDocuments = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.OUTGOING_DOCUMENTS}/generate`,
    options: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lastNDays }),
    },
  });

  return {
    outgoingDocuments,
  };
}

type JudgeEsFileStat = {
  date: Date;
  year: number;
  courtFileCount: {
    totalCount: number;
    unresolvedSenateCount: number;
    unresolvedPlenaryCount: number;
    resolvedSenateCount: number;
    resolvedPlenaryCount: number;
  };
  plenaryOpinionCount: {
    totalCount: number;
    unresolvedCount: number;
    resolvedCount: number;
  };
  disciplinaryProceedingsCount: {
    totalCount: number;
    unresolvedCount: number;
    resolvedCount: number;
  };
};

export type FormattedEsFilesBar = {
  label: string;
  value: number;
  courtFileType?: CourtFileType;
  state: 'RESOLVED' | 'UNRESOLVED';
};

type FormattedEsFilesStats = {
  date: Date;
  year: number;
  data: {
    [agenda: Agenda[number]]: {
      totalCount: number;
      unresolved: FormattedEsFilesBar[];
      resolved: FormattedEsFilesBar[];
    };
  };
};

type AssistantEsFileStat = {
  date: Date;
  year: number;
  assistantFileCounts: {
    assistant: string;
    assistantId: string;
    totalCount: number;
    unresolvedSenateCount: number;
    unresolvedPlenaryCount: number;
    resolvedSenateCount: number;
    resolvedPlenaryCount: number;
  }[];
};

export type FormattedAssistantBar = {
  label: string;
  value: number;
  type: CourtFileType;
  state: 'RESOLVED' | 'UNRESOLVED';
  assistantId: string;
};

export type FormattedAssistantEsFilesStats = {
  date: Date;
  year: number;
  data: {
    assistant: string;
    assistantId: string;
    totalCount: number;
    unresolved: FormattedAssistantBar[];
    resolved: FormattedAssistantBar[];
  }[];
};

export function useAssistantEsFilesStatistics(year?: number) {
  const [formattedData, setFormattedData] =
    useState<FormattedAssistantEsFilesStats>({
      data: [],
    } as unknown as FormattedAssistantEsFilesStats);

  const url =
    year === undefined
      ? `${EvidenceApiUrl.STATISTICS_JUDGES_ASSISTANTS_ES_FILES}/data`
      : `${EvidenceApiUrl.STATISTICS_JUDGES_ASSISTANTS_ES_FILES}/data?year=${year}`;

  const [result, loading] = useFetch<AssistantEsFileStat>(url);

  useEffect(() => {
    if (!loading && result) {
      setFormattedData({
        date: result.date,
        year: result.year,
        data: result.assistantFileCounts.map((item) => ({
          assistant: item.assistant,
          assistantId: item.assistantId,
          totalCount: item.totalCount,
          unresolved: [
            {
              label: 'Nevyřízené senátní',
              value: item.unresolvedSenateCount,
              type: CourtFileType.SENATE,
              state: 'UNRESOLVED',
              assistantId: item.assistantId,
            },
            {
              label: 'Nevyřízené plenární',
              value: item.unresolvedPlenaryCount,
              type: CourtFileType.PLENARY,
              state: 'UNRESOLVED',
              assistantId: item.assistantId,
            },
          ],
          resolved: [
            {
              label: 'Vyřízené senátní',
              value: item.resolvedSenateCount,
              type: CourtFileType.SENATE,
              state: 'RESOLVED',
              assistantId: item.assistantId,
            },
            {
              label: 'Vyřízené plenární',
              value: item.resolvedPlenaryCount,
              type: CourtFileType.PLENARY,
              state: 'RESOLVED',
              assistantId: item.assistantId,
            },
          ],
        })),
      });
    }
  }, [loading, result, year]);

  return {
    result,
    loading,
    formattedData,
  };
}

type CurrentAssistantEsFileStat = {
  date: string;
  year?: number | null;
  assistant: string;
  assistantId: string | null;
  courtFileCount: JudgeEsFileStat['courtFileCount'];
};

type FormattedCurrentAssistantEsFilesStats = Omit<
  FormattedAssistantEsFilesStats,
  'year'
> & { year: number | null };

export function useCurrentAssistantEsFilesStatistics(year?: number) {
  const url = `${EvidenceApiUrl.STATISTICS_CURRENT_ASSISTANT_ES_FILES}/data${
    year === undefined ? '' : `?year=${year}`
  }`;
  const [result, loading] = useFetch<CurrentAssistantEsFileStat>(url);
  const formattedData = useMemo<FormattedCurrentAssistantEsFilesStats>(() => {
    const data: FormattedAssistantEsFilesStats['data'] = [];
    if (
      !loading &&
      result?.assistantId &&
      (result.year ?? null) === (year ?? null)
    ) {
      const { assistant, assistantId, courtFileCount: counts } = result;
      data.push({
        assistant,
        assistantId,
        totalCount: counts.totalCount,
        unresolved: [
          {
            label: 'Nevyřízené senátní',
            value: counts.unresolvedSenateCount,
            type: CourtFileType.SENATE,
            state: 'UNRESOLVED',
            assistantId,
          },
          {
            label: 'Nevyřízené plenární',
            value: counts.unresolvedPlenaryCount,
            type: CourtFileType.PLENARY,
            state: 'UNRESOLVED',
            assistantId,
          },
        ],
        resolved: [
          {
            label: 'Vyřízené senátní',
            value: counts.resolvedSenateCount,
            type: CourtFileType.SENATE,
            state: 'RESOLVED',
            assistantId,
          },
          {
            label: 'Vyřízené plenární',
            value: counts.resolvedPlenaryCount,
            type: CourtFileType.PLENARY,
            state: 'RESOLVED',
            assistantId,
          },
        ],
      });
    }
    return {
      date: result ? new Date(result.date) : new Date(),
      year: year ?? null,
      data,
    };
  }, [result, loading, year]);

  return { result, loading, formattedData };
}

export function useJudgeEsFilesStatistics(year?: number) {
  const [formattedData, setFormattedData] = useState<FormattedEsFilesStats>();
  const url =
    year === undefined
      ? `${EvidenceApiUrl.STATISTICS_JUDGES_ES_FILES}/data`
      : `${EvidenceApiUrl.STATISTICS_JUDGES_ES_FILES}/data?year=${year}`;

  const [result, loading] = useFetch<JudgeEsFileStat>(url);

  useEffect(() => {
    if (!loading && result) {
      setFormattedData({
        date: result.date,
        year: result.year,
        data: {
          [Agenda.COURT]: {
            totalCount: result.courtFileCount.totalCount,
            unresolved: [
              {
                label: 'Nevyřízené senátní',
                value: result.courtFileCount.unresolvedSenateCount,
                courtFileType: CourtFileType.SENATE,
                state: 'UNRESOLVED',
              },
              {
                label: 'Nevyřízené plenární',
                value: result.courtFileCount.unresolvedPlenaryCount,
                courtFileType: CourtFileType.PLENARY,
                state: 'UNRESOLVED',
              },
            ],
            resolved: [
              {
                label: 'Vyřízené senátní',
                value: result.courtFileCount.resolvedSenateCount,
                courtFileType: CourtFileType.SENATE,
                state: 'RESOLVED',
              },
              {
                label: 'Vyřízené plenární',
                value: result.courtFileCount.resolvedPlenaryCount,
                courtFileType: CourtFileType.PLENARY,
                state: 'RESOLVED',
              },
            ],
          },
          [Agenda.PLENARY_OPINION]: {
            totalCount: result.plenaryOpinionCount.totalCount,
            unresolved: [
              {
                label: 'Nevyřízené',
                value: result.plenaryOpinionCount.unresolvedCount,
                state: 'UNRESOLVED',
              },
            ],
            resolved: [
              {
                label: 'Vyřízené',
                value: result.plenaryOpinionCount.resolvedCount,
                state: 'RESOLVED',
              },
            ],
          },
          [Agenda.DISCIPLINARY_PROCEEDINGS]: {
            totalCount: result.disciplinaryProceedingsCount.totalCount,
            unresolved: [
              {
                label: 'Nevyřízené',
                value: result.disciplinaryProceedingsCount.unresolvedCount,
                state: 'UNRESOLVED',
              },
            ],
            resolved: [
              {
                label: 'Vyřízené',
                value: result.disciplinaryProceedingsCount.resolvedCount,
                state: 'RESOLVED',
              },
            ],
          },
        },
      });
    }
  }, [loading, result, year]);

  return {
    result,
    loading,
    formattedData,
  };
}

export function useUserOverviewStatistics(isJudge = false) {
  const activeFilesAccordingToAssistants = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.ACTIVE_FILES_ACCORDING_TO_ASSISTANTS}`,
  });

  const courtFilesWithRunningDeadline = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.COURT_FILES_WITH_RUNNING_DEADLINE}`,
    skip: isJudge,
  })?.[0];

  const unfinishedTasksForUser = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.UNFINISHED_TASKS_FOR_USER}`,
  })?.[0];

  const unprocessedDeliveredDocuments = useReport({
    url: `${EvidenceApiUrl.REPORTS}/${ReportDefinition.UNPROCESSED_DELIVERED_DOCUMENTS_COUNT}`,
  })?.[0];

  return {
    activeFilesAccordingToAssistants,
    courtFilesWithRunningDeadline,
    unfinishedTasksForUser,
    unprocessedDeliveredDocuments,
  };
}

export function useSetCustomRange(setValue: (value: number) => void) {
  const intl = useIntl();
  const PROMPT_KEY = 'SET_CUSTOM_RANGE';

  const validationSchema = useValidationSchema();

  const { testPrompt } = useContext(PromptContext);

  const handleConfirmDialog = useEventCallback((value) => {
    setValue(parseInt(value.lastNDays));
  });

  usePrompts([
    {
      key: PROMPT_KEY,
      dialogTitle: intl.formatMessage({
        id: 'ES__DASHBOARD__DOCUMENTS__SET_CUSTOM_RANGE__TITLE',
        defaultMessage: 'Nastavení rozsahu',
      }),
      dialogText: '',
      FormFields: function FormFields() {
        return (
          <FormTextField label="Počet dní" type="number" name="lastNDays" />
        );
      },
      formValidationSchema: validationSchema,
    },
  ]);

  const handleSetCustomRange = useEventCallback(() => {
    testPrompt({
      key: PROMPT_KEY,
      submitCallback: handleConfirmDialog,
    });
  });

  return { handleSetRange: handleSetCustomRange };
}
